import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useShoppingCart } from '../../contexts/ShoppingCartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer, CreateCustomerRequest } from '../../types';
import { apiClient as api } from '../../services/api';
import { rateLimitErrorMessage } from '../../utils/errorMessage';
import { formatCustomerError } from '../../utils/customerErrorMessage';
import { useCustomerMutations } from '../../hooks/useCustomerMutations';

interface CustomerSelectorProps {
  onCustomerSelected?: (customer: Customer) => void;
}

export function CustomerSelector({ onCustomerSelected }: CustomerSelectorProps) {
  const { state, selectCustomer } = useShoppingCart();
  const { t } = useLanguage();
  const { saveCustomerMutation } = useCustomerMutations();
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Create customer dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: ''
  });
  const isCreating = saveCustomerMutation.isPending;

  const searchCustomers = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setCustomers([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Use search API instead of loading all customers
      const response = await api.searchCustomers({ name: searchTerm });
      setCustomers(response);
    } catch (error: unknown) {
      setError(rateLimitErrorMessage(error, t('sales.failedToLoadCustomers'), t));
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => customers.filter(customer =>
      `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
    ), [customers, searchTerm]);

  const handleCustomerSelect = (customer: Customer) => {
    selectCustomer(customer);
    if (onCustomerSelected) {
      onCustomerSelected(customer);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.firstName || !newCustomer.lastName) {
      return;
    }

    try {
      const createdCustomer = await saveCustomerMutation.mutateAsync({
        data: {
          firstName: newCustomer.firstName,
          lastName: newCustomer.lastName,
          email: newCustomer.email || undefined,
          phone: newCustomer.phone || undefined,
          address: newCustomer.address || undefined,
        } as CreateCustomerRequest,
      });

      setCustomers(prev => [createdCustomer, ...prev]);
      setShowCreateDialog(false);
      setNewCustomer({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: ''
      });
      
      // Auto-select the newly created customer
      handleCustomerSelect(createdCustomer);
    } catch (error: unknown) {
      setError(formatCustomerError(error, t('sales.failedToCreateCustomer'), t));
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2, background: theme => `linear-gradient(112deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})` }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: 2, 
            mb: 2 
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main' }}><PersonIcon fontSize="small" /></Box>
              {t('sales.selectCustomer')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              {t('sales.newCustomer')}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {state.selectedCustomer && (
            <Alert severity="success" sx={{ '& .MuiAlert-message': { width: '100%' }, border: theme => `1px solid ${theme.palette.success.main}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {state.selectedCustomer.firstName} {state.selectedCustomer.lastName}
                  </Typography>
                  {state.selectedCustomer.phone && (
                    <Typography variant="body2" color="text.secondary">
                      {state.selectedCustomer.phone}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => selectCustomer(null)}
                >
                  {t('sales.changeCustomer')}
                </Button>
              </Box>
            </Alert>
          )}

          {!state.selectedCustomer && (
            <Autocomplete
              options={filteredCustomers}
              getOptionLabel={(customer) => `${customer.firstName} ${customer.lastName}`}
              renderOption={(props, customer) => {
                const { key, ...optionProps } = props;
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ p: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Typography variant="body1" sx={{ display: 'block', fontWeight: 'medium' }}>
                        {customer.firstName} {customer.lastName}
                      </Typography>
                      {customer.phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                          {customer.phone}
                        </Typography>
                      )}
                      {!customer.phone && customer.email && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                          {customer.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'sales-customer-search',
                  }}
                  label={t('sales.searchCustomers')}
                  placeholder={t('sales.typeToSearchCustomer')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onInputChange={(event, value) => {
                setSearchTerm(value);
                if (value.length > 0) {
                  searchCustomers(value);
                } else {
                  setCustomers([]);
                }
              }}
              onChange={(event, customer) => {
                if (customer) {
                  handleCustomerSelect(customer);
                }
              }}
              loading={isLoading}
              noOptionsText={searchTerm ? t('sales.noCustomersFound') : t('sales.typeToSearchCustomer')}
              sx={{ maxWidth: 640 }}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Customer Dialog */}
      <AppDialog presentation="compact" open={showCreateDialog} onClose={() => setShowCreateDialog(false)} closeDisabled={isCreating} maxWidth="sm" fullWidth>
        <DialogTitle>{t('sales.createCustomer')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t('customers.firstName')}
                value={newCustomer.firstName}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label={t('customers.lastName')}
                value={newCustomer.lastName}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </Stack>
            <TextField
              fullWidth
              label={t('customers.email')}
              type="email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              fullWidth
              label={t('customers.phone')}
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
            />
            <TextField
              fullWidth
              label={t('customers.address')}
              multiline
              rows={2}
              value={newCustomer.address}
              onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={isCreating || !newCustomer.firstName || !newCustomer.lastName}
            startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {isCreating ? t('sales.creating') : t('sales.createCustomer')}
          </Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
